import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Loader2, FileText } from 'lucide-react';
import { HttpError, type CreateReferralRequest } from '@vagusx/clients';
import { useCreateReferral } from '@/hooks/use-documents';
import { useProviders } from '@/hooks/use-providers';
import { useUnsavedChanges } from '@/hooks/use-unsaved-changes';
import { getDocumentClient } from '@/lib/api-clients';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UnsavedChangesDialog } from '@/components/custom/unsaved-changes-dialog';

/** Select value for "no sex given"; Radix Select items cannot use ''. */
const NO_SEX = 'unspecified';

// DOB is required up front because the patient is created in the EHR (Athena),
// which needs it.
const formSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  dateOfBirth: z.string().min(1, 'Date of birth is required'),
  // Only the two values athena's `sex` can hold — offering more would let a choice
  // silently not reach the EHR.
  gender: z.enum(['female', 'male']).optional(),
  phone: z.string().optional(),
  memberId: z.string().optional(),
  address: z.string().optional(),
  renderingProviderId: z.string().optional(),
  referringProviderName: z.string().optional(),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const trimmed = (v: string | undefined): string | undefined => {
  const t = v?.trim();
  return t ? t : undefined;
};

export function NewReferralDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const navigate = useNavigate();
  const createReferral = useCreateReferral();
  const { data: providersResult } = useProviders();
  const providers = providersResult?.items ?? [];

  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  // 'pdf' mode auto-fills the fields from an uploaded referral PDF; 'manual' is
  // straight data entry. Either way the same fields are submitted (createInEhr).
  const [mode, setMode] = useState<'manual' | 'pdf'>('manual');
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(false);
  // Shown when the user tries to close the dialog with entered-but-unsaved data.
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // Explicit empty defaults so `reset()` returns to a known-clean state and
  // `formState.isDirty` reflects "the user entered something".
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: undefined,
      phone: '',
      memberId: '',
      address: '',
      renderingProviderId: '',
      referringProviderName: '',
      notes: '',
    },
  });

  // Wipe the form + PDF/mode state so the dialog opens fresh next time (rather
  // than remembering the previous interaction's values).
  const resetAll = (): void => {
    form.reset();
    setPdfFile(null);
    setExtracted(false);
    setMode('manual');
  };

  const hasUnsavedInput = form.formState.isDirty || pdfFile !== null;
  // Also guard a browser tab-close / refresh (beforeunload) while there's unsaved
  // input — the in-app close paths prompt via UnsavedChangesDialog, but only this
  // covers leaving the page entirely.
  useUnsavedChanges(false, hasUnsavedInput);

  // Intercept every close path (Cancel, ✕, Esc, outside-click). Guard against
  // losing entered data; on a clean form just reset and close.
  const requestClose = (): void => {
    if (busy || extracting) return;
    if (hasUnsavedInput) {
      setConfirmDiscardOpen(true);
      return;
    }
    resetAll();
    onOpenChange(false);
  };

  const confirmDiscard = (): void => {
    setConfirmDiscardOpen(false);
    resetAll();
    onOpenChange(false);
  };

  // Upload a PDF and prefill the form from the extracted fields. The file is kept
  // in `pdfFile` so it's still attached to the document after creation. Only fields
  // the extractor actually found are written — the user reviews/edits before saving.
  const handlePdfSelected = async (file: File | null): Promise<void> => {
    if (!file) {
      setPdfFile(null);
      return;
    }
    // Start clean so a re-pick or a failed extraction can't leave a prior PDF's
    // fields behind (which would create a patient whose details don't match the
    // attached document).
    form.reset();
    setExtracted(false);
    setPdfFile(file);
    setExtracting(true);
    try {
      const { patient, referral } = await getDocumentClient().extractReferralFromPdf(file);
      const setIf = (field: keyof FormValues, value: string | undefined): void => {
        if (value) form.setValue(field, value, { shouldDirty: true, shouldValidate: true });
      };
      setIf('firstName', patient.firstName);
      setIf('lastName', patient.lastName);
      setIf('dateOfBirth', patient.dateOfBirth);
      setIf('phone', patient.phone);
      setIf('memberId', patient.memberId);
      setIf('address', patient.address);
      setIf('referringProviderName', referral.referringProviderName);
      setIf('notes', referral.notes);
      setExtracted(true);
      toast.success('Fields extracted from PDF — review before creating');
    } catch {
      // Drop the unreadable file so we don't attach it to a manually-entered
      // referral and so the close-guard doesn't treat it as unsaved input.
      setPdfFile(null);
      toast.error('Could not read that PDF; switch to manual entry or try another file');
    } finally {
      setExtracting(false);
    }
  };

  const onSubmit = async (values: FormValues): Promise<void> => {
    setBusy(true);
    const payload: CreateReferralRequest = {
      patient: {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        dateOfBirth: values.dateOfBirth,
        gender: values.gender,
        phone: trimmed(values.phone),
        memberId: trimmed(values.memberId),
        address: trimmed(values.address),
      },
      referral: {
        renderingProviderId: values.renderingProviderId || undefined,
        referringProviderName: trimmed(values.referringProviderName),
        notes: trimmed(values.notes),
      },
      // The patient is created in the EHR (Athena) so it carries an EHR id and the
      // appointment can sync when scheduled.
      patientLink: { mode: 'createInEhr' },
    };

    try {
      const { document } = await createReferral.mutateAsync(payload);
      // Attach the PDF after the doc exists (best-effort — the referral is already
      // created, so a failed upload shouldn't lose the structured data).
      if (pdfFile) {
        try {
          await getDocumentClient().uploadPdf(document.id, pdfFile);
        } catch {
          toast.warning('Referral created, but the PDF upload failed');
        }
      }
      toast.success('Referral created');
      onOpenChange(false);
      resetAll();
      navigate({ to: '/documents/$id', params: { id: document.id } });
    } catch (err) {
      if (err instanceof HttpError && err.isValidation) {
        let mapped = 0;
        for (const fe of err.fieldErrors) {
          // Map server field paths (patient.firstName) to the flat form field.
          const field = fe.field.split('.').pop() ?? fe.field;
          if (field in form.getValues()) {
            form.setError(field as keyof FormValues, { message: fe.message });
            mapped++;
          }
        }
        // If nothing mapped to a visible field, fall through so the error is
        // surfaced (a silent return would leave the user with no feedback).
        if (mapped > 0) return;
      }
      throw err; // global MutationCache handler toasts the rest
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : requestClose())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New referral</DialogTitle>
          <DialogDescription>
            Create a referral and its patient in the EHR — enter the details manually or upload a
            referral PDF to auto-fill them.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={(v) => setMode(v as 'manual' | 'pdf')} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="manual">Enter manually</TabsTrigger>
            <TabsTrigger value="pdf">From PDF</TabsTrigger>
          </TabsList>
        </Tabs>

        {mode === 'pdf' && (
          <div className="grid gap-1.5 rounded-md border border-dashed p-3">
            <Label className="text-xs text-muted-foreground">Referral PDF</Label>
            <div className="flex items-center gap-2">
              <Input
                type="file"
                accept="application/pdf"
                disabled={extracting}
                onChange={(e) => void handlePdfSelected(e.target.files?.[0] ?? null)}
                className="cursor-pointer"
              />
              {extracting && (
                <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Extracting…
                </span>
              )}
              {!extracting && pdfFile && (
                <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                  <FileText className="h-3.5 w-3.5" />
                  {pdfFile.name}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {extracted
                ? 'Review the auto-filled fields below and edit anything that looks wrong before creating.'
                : 'Upload a referral PDF to auto-fill the fields below.'}
            </p>
          </div>
        )}

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" error={form.formState.errors.firstName?.message}>
              <Input {...form.register('firstName')} placeholder="James" />
            </Field>
            <Field label="Last name" error={form.formState.errors.lastName?.message}>
              <Input {...form.register('lastName')} placeholder="Smith" />
            </Field>
            <Field label="Date of birth" error={form.formState.errors.dateOfBirth?.message}>
              <Input type="date" {...form.register('dateOfBirth')} />
            </Field>
            <Field label="Sex">
              <Select
                value={form.watch('gender') ?? ''}
                onValueChange={(v) =>
                  form.setValue('gender', v === NO_SEX ? undefined : (v as 'female' | 'male'), {
                    shouldDirty: true,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select sex" />
                </SelectTrigger>
                <SelectContent>
                  {/* A mistaken choice must be undoable, not only swappable. */}
                  <SelectItem value={NO_SEX}>Not specified</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Phone">
              <Input {...form.register('phone')} placeholder="+1 650 555 0142" />
            </Field>
            <Field label="Member ID">
              <Input {...form.register('memberId')} placeholder="Insurance member ID" />
            </Field>
            <Field label="Address" className="col-span-2">
              <Input {...form.register('address')} placeholder="Street, city, ST zip" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Rendering provider">
              <Select
                value={form.watch('renderingProviderId') ?? ''}
                onValueChange={(v) => form.setValue('renderingProviderId', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Referring provider">
              <Input {...form.register('referringProviderName')} placeholder="Dr. Jane Doe" />
            </Field>
          </div>

          <Field label="Notes / reason">
            <Textarea
              {...form.register('notes')}
              placeholder="Low back pain, eval and treat"
              rows={2}
            />
          </Field>

          {mode === 'manual' && (
            <Field label="Referral PDF (optional)">
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
                  className="cursor-pointer"
                />
                {pdfFile && (
                  <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                    <FileText className="h-3.5 w-3.5" />
                    {pdfFile.name}
                  </span>
                )}
              </div>
            </Field>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={requestClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || extracting}>
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Create referral
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <UnsavedChangesDialog
        open={confirmDiscardOpen}
        onDiscard={confirmDiscard}
        onCancel={() => setConfirmDiscardOpen(false)}
      />
    </Dialog>
  );
}

function Field({
  label,
  error,
  className,
  children,
}: {
  label: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={cn('grid gap-1.5', className)}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
